import { html, LitElement, TemplateResult } from "lit";
import { customElement, property, queryAssignedNodes } from "lit/decorators.js";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import { Translatable } from "@slhui/i18n/src/mixins/translatable";
import { SlhIcon } from "@slhuiicon/icon";
import { style } from "./assets/content-notification.generated-style";

import {
  CLOSE_ICON_TYPE,
  ALERT_ICON_TYPE,
  EVENT_CONTENT_NOTIFICATION_CLOSED,
} from "./content-notification.constants";

/**
 * Draw user's attention to alerts or important information such as system changes. The type and urgency of the information is shown in color.
 *
 * @CustomElement slh-content-notification
 *
 * @cssprop --slh-default-font-family - Content notification font family.
 * @cssprop --slh-base-xxs-line-height - Content notification line height.
 * @cssprop --slh-base-xs-font-size - Content notification font size.
 * @cssprop --slh-color-neutral-shade-80 - Content notification content font color.
 * @cssprop --slh-base-semi-bold-font-weight - Content notification content font weight.
 * @cssprop --slh-color-neutral - Content notification title font color.
 * @cssprop --slh-base-bold-font-weight - Content notification title font weight.
 * @cssprop --slh-color-info-shade-20 - Info theme background color.
 * @cssprop --slh-color-info - Info theme icon color.
 * @cssprop --slh-color-info-shade-80 - Info theme border color.
 * @cssprop --slh-color-success-shade-20 - Success theme background color.
 * @cssprop --slh-color-success - Success theme icon color.
 * @cssprop --slh-color-success-shade-80 - Success theme border color.
 * @cssprop --slh-color-warning-shade-20 - Warning theme background color.
 * @cssprop --slh-color-warning - Warning theme icon color.
 * @cssprop --slh-color-warning-shade-80 - Warning theme border color.
 * @cssprop --slh-color-error-shade-20 - Error theme background color.
 * @cssprop --slh-color-error - Error theme icon color.
 * @cssprop --slh-color-error-shade-80 - Error theme border color.
 * @cssprop --slh-color-neutral-shade-60 - Close icon color.
 *
 * slots
 * @slot default - Notification content
 * @slot title - Notification title
 */
@customElement("slh-content-notification")
export default class SlhContentNotification extends Translatable(LitElement) {
  @queryAssignedNodes({ slot: "close-icon" })
  private closeIconSlotNodes: Array<HTMLElement> | undefined;

  /**
   * Add info styling to the content notification
   */
  @property({
    type: Boolean,
    attribute: true,
  })
  info: boolean = true;

  /**
   * Add success styling to the content notification
   */
  @property({
    type: Boolean,
    attribute: true,
  })
  success: boolean = false;

  /**
   * Add warning styling to the content notification
   */
  @property({
    type: Boolean,
    attribute: true,
  })
  warning: boolean = false;

  /**
   * Add warning styling to the content notification
   */
  @property({
    type: Boolean,
    attribute: true,
  })
  error: boolean = false;

  /**
   * Forces the mobile appearance of the component
   */
  @property({
    type: Boolean,
    attribute: "one-column",
    reflect: true,
  })
  oneColumn: boolean = false;

  /**
   * Used for setting id attribute value to the element, if not already set
   * @private
   */
  static instanceCount = 0;

  static styles = style;

  firstUpdated() {
    this.setAttribute("role", "dialog");
    this.setAttribute("tabindex", "0");
    if (!this.hasAttribute("id")) {
      this.id = `slh-content-notification-${(SlhContentNotification.instanceCount += 1)}`;
    }
    this.setCloseIcon();
  }

  renderClasses(): ClassInfo {
    return {
      info: this.info,
      success: this.success,
      warning: this.warning,
      error: this.error,
      "one-column": this.oneColumn,
    };
  }

  /**
   * Hide content notification
   */
  public closeNotification() {
    this.classList.add("content-notification-closed");
    const event = new CustomEvent(EVENT_CONTENT_NOTIFICATION_CLOSED, {
      detail: {
        id: this.id,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  /**
   * @param {KeyboardEvent} event
   * w3.org keyboard support
   */
  protected handleKeydown(event: KeyboardEvent) {
    const { code } = event;
    if (code === "Enter" || code === "NumpadEnter" || code === "Backspace") {
      this.closeNotification();
    }
  }

  /**
   * Append clearable icon to close-icon slot
   */
  private setCloseIcon(): void {
    const icon: SlhIcon = new SlhIcon();
    icon.focusable = true;
    icon.slot = "close-icon";
    icon.setAttribute("aria-label", this.trans("content-notification.close"));
    icon.classList.add("close-icon");
    icon.type = CLOSE_ICON_TYPE;
    icon.onkeydown = this.handleKeydown.bind(this);
    this.closeIconSlotNodes?.forEach((iconAppended) => {
      iconAppended.remove();
    });
    icon.addEventListener("click", (event) => {
      if (event instanceof CustomEvent) {
        this.closeNotification();
      }
    });
    this.appendChild(icon);
  }

  /**
   *
   * @returns {TemplateResult} rendered notification
   */
  render(): TemplateResult {
    return html`<div
      class="content-notification-holder ${classMap(this.renderClasses())}"
    >
      <slh-icon class="alert-icon" type="${ALERT_ICON_TYPE}"></slh-icon>
      <div class="content-notification">
        <slot name="title"></slot>
        <slot></slot>
      </div>
      <slot name="close-icon"></slot>
    </div>`;
  }
}
